import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { AiCreditsStatus } from '@findmystuff/shared';

export function useAiCredits() {
  const queryClient = useQueryClient();

  const {
    data: creditsStatus,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['aiCredits'],
    queryFn: () => api.admin.getCreditsStatus(),
    staleTime: 30000, // 30 seconds
  });

  const useCredit = useMutation({
    mutationFn: () => api.admin.useCredit(),
    onSuccess: (newStatus) => {
      queryClient.setQueryData(['aiCredits'], newStatus);
    },
  });

  const consumeCredit = async (): Promise<{ success: boolean; status: AiCreditsStatus | null }> => {
    try {
      const newStatus = await useCredit.mutateAsync();
      return { success: true, status: newStatus };
    } catch (error) {
      // Refresh credits status in case it's out of sync
      queryClient.invalidateQueries({ queryKey: ['aiCredits'] });
      return { success: false, status: null };
    }
  };

  return {
    creditsStatus,
    isLoading,
    error,
    hasCredits: creditsStatus?.hasCredits ?? false,
    remaining: creditsStatus?.remaining ?? 0,
    used: creditsStatus?.used ?? 0,
    limit: creditsStatus?.limit ?? 0,
    isUnlimited: creditsStatus?.limit === -1,
    consumeCredit,
    isConsuming: useCredit.isPending,
  };
}
